import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Plus, AlertTriangle, CheckCircle2, Clock, Shield, Paperclip, ChevronRight, Loader2,
} from 'lucide-react';
import { CATEGORY_MAP, getCategoryConfig, DEFAULT_LEGEND } from '../lib/categories';
import { useBoards } from '../context/BoardContext';
import { listIssues } from '../lib/api';
import IssueEditDialog from './IssueEditDialog';
import { usePermissions } from '../context/PermissionsContext';

/**
 * IssueListDialog: shown when clicking a mosaic cell.
 * Lists all issues on that (date, category), and allows adding new issues.
 */
const IssueListDialog = ({ open, onOpenChange, categoryKey, date, onChanged }) => {
  const { activeBoard } = useBoards();
  const perms = usePermissions();
  const canEdit = perms.canEdit(categoryKey);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState({ open: false, existing: null });
  const cat = CATEGORY_MAP[categoryKey];
  const catConfig = getCategoryConfig(activeBoard, categoryKey);
  const statuses = catConfig?.statuses || DEFAULT_LEGEND;
  const legendMap = statuses.reduce((acc, s) => {
    acc[s.key] = s;
    return acc;
  }, {});

  const load = useCallback(async () => {
    if (!open || !activeBoard || !date || !categoryKey) {
      setIssues([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listIssues(activeBoard.id, {
        category: categoryKey,
        start_date: date,
        end_date: date,
      });
      setIssues(data);
    } finally {
      setLoading(false);
    }
  }, [open, activeBoard, date, categoryKey]);

  useEffect(() => {
    load();
  }, [load]);

  if (!cat) return null;

  const handleAdd = () => {
    setEditor({ open: true, existing: null });
  };

  const handleEdit = (issue) => {
    setEditor({ open: true, existing: issue });
  };

  const handleEditorClose = (changed) => {
    setEditor({ open: false, existing: null });
    if (changed) {
      load();
      onChanged && onChanged();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center text-white text-sm font-bold"
                style={{ background: cat.color }}
              >
                {cat.key}
              </div>
              <div>
                <div>{cat.label}</div>
                <DialogDescription className="text-xs mt-0.5">
                  {date} · {issues.length} {issues.length === 1 ? 'entry' : 'entries'}
                </DialogDescription>
              </div>
              <Button
                onClick={handleAdd}
                className="ml-auto bg-[#0090D8] hover:bg-[#0079B5] text-white h-9"
                disabled={!canEdit}
                title={!canEdit ? 'You are not assigned to this category' : ''}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New issue
              </Button>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading...
            </div>
          ) : issues.length === 0 ? (
            <div className="py-10 text-center">
              <div className="text-slate-400 mb-3 text-sm">
                No issues logged for this date yet.
              </div>
              <Button onClick={handleAdd} variant="outline">
                <Plus className="h-4 w-4 mr-1.5" />
                Log first issue
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {issues.map((issue) => {
                const status = legendMap[issue.color] || legendMap.grey || DEFAULT_LEGEND[3];
                const hasTemp = (issue.temporary_cm || '').length > 0;
                const hasPerm = (issue.permanent_cm || '').length > 0;
                return (
                  <button
                    key={issue.id}
                    onClick={() => handleEdit(issue)}
                    className={`w-full rounded-lg border-2 p-3 text-left transition-all hover:shadow-md hover:-translate-y-0.5 group ${
                      issue.is_closed ? 'opacity-70' : ''
                    }`}
                    style={{
                      background: status.bg,
                      borderColor: status.ring || status.color,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0 mt-0.5"
                        style={{ background: status.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: status.ring || status.color }}
                          >
                            {status.label}
                          </span>
                          {issue.repeat_issue && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Repeat
                            </span>
                          )}
                          {issue.is_closed && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300">
                              <CheckCircle2 className="h-2.5 w-2.5" />
                              Closed
                            </span>
                          )}
                          {hasTemp && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300">
                              <Clock className="h-2.5 w-2.5" />
                              Temp CM
                            </span>
                          )}
                          {hasPerm && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">
                              <Shield className="h-2.5 w-2.5" />
                              Perm CM
                            </span>
                          )}
                        </div>
                        {/* Details preview */}
                        {Object.keys(issue.details || {}).length > 0 && (
                          <div className="text-sm text-slate-800 font-medium mt-1 line-clamp-1">
                            {Object.values(issue.details)
                              .filter((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)))
                              .map((v) => (Array.isArray(v) ? v.join(', ') : v))
                              .slice(0, 3)
                              .join(' · ')}
                          </div>
                        )}
                        {issue.notes && (
                          <div className="text-xs text-slate-600 mt-0.5 line-clamp-1">
                            {issue.notes}
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1.5">
                          {issue.owner_email && (
                            <span>👤 {issue.owner_email}</span>
                          )}
                          {issue.target_date && (
                            <span>📅 Due {issue.target_date}</span>
                          )}
                          <span>Status: {issue.status}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1 group-hover:text-slate-600" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <IssueEditDialog
        open={editor.open}
        onOpenChange={(v) => {
          if (!v) handleEditorClose(false);
        }}
        onSaved={() => handleEditorClose(true)}
        onDeleted={() => handleEditorClose(true)}
        categoryKey={categoryKey}
        date={date}
        existing={editor.existing}
      />
    </>
  );
};

export default IssueListDialog;
